import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import './styles/tokens.css';
import './theme/theme.css';
import App from './App.tsx';
import { SessionProvider } from './auth/SessionProvider';
import { purgeLegacyThemePreference } from './theme/lockedTheme';
import { initPosthog } from './lib/posthog';
import { registerSW } from 'virtual:pwa-register';

// APPEARANCE IS LOCKED to outdoor high-contrast (Steve, 2026-08-06). The
// Light/Dark/Outdoor switcher is gone; this erases any 'rhody-theme' value a
// returning owner still has stored so the retired setting cannot come back.
purgeLegacyThemePreference();

// Optional error tracking. NO-OP unless VITE_SENTRY_DSN is baked into the build
// (.env.production). Local/dev builds leave it unset so nothing is reported.
// No PII (send_default_pii off) -- we handle TCPA/customer data. No perf tracing
// (tracesSampleRate 0) to stay well inside the free tier.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'production',
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    tracesSampleRate: 0,
    sendDefaultPii: false,
  });
}

// Optional product analytics. NO-OP unless VITE_POSTHOG_KEY is baked into the
// build (.env.production). Local/dev builds leave it unset, so the posthog-js
// chunk never loads and nothing is captured. VITE_POSTHOG_HOST defaults to the
// US cloud ingest host. Mirrors the Sentry gate above.
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
if (POSTHOG_KEY) {
  void initPosthog(POSTHOG_KEY, import.meta.env.VITE_POSTHOG_HOST as string | undefined);
}

// PWA service worker (vite-plugin-pwa, registerType: autoUpdate).
registerSW({ immediate: true });

const queryClient = new QueryClient();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <App />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
